import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuditEntry {
  id: string;
  user_id: string | null;
  action: string;
  resource: string;
  status_code: number;
  ip_address: string;
  duration_ms: number;
  timestamp: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AuditLog() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [resource, setResource] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const params: Record<string, string> = { page: String(page), limit: "50" };
  if (action) params.action = action;
  if (resource) params.resource = resource;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const { data, isLoading } = useQuery<{
    data: AuditEntry[];
    meta: { total: number; page: number; limit: number };
  }>({
    queryKey: ["audit-log", page, action, resource, startDate, endDate],
    queryFn: () => api.get("/admin/audit/audit-log", { params }),
  });

  const entries = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / 50);

  function getMethodColor(method: string) {
    switch (method) {
      case "GET":
        return "bg-blue-100 text-blue-800";
      case "POST":
        return "bg-green-100 text-green-800";
      case "PUT":
      case "PATCH":
        return "bg-yellow-100 text-yellow-800";
      case "DELETE":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  }

  function getStatusColor(code: number) {
    if (code >= 200 && code < 300) return "text-green-600";
    if (code >= 400 && code < 500) return "text-yellow-600";
    if (code >= 500) return "text-red-600";
    return "text-muted-foreground";
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Audit Log</h2>
        <p className="text-sm text-muted-foreground">
          Review all system activity and API requests
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Method</label>
              <select
                value={action}
                onChange={(e) => { setAction(e.target.value); setPage(1); }}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">All Methods</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <Input
              label="Resource"
              value={resource}
              onChange={(e) => { setResource(e.target.value); setPage(1); }}
              placeholder="e.g. /api/clients"
            />
            <Input
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            />
            <Input
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            />
          </div>
        </CardContent>
      </Card>

      {isLoading && <div className="h-64 animate-pulse rounded-lg border bg-muted" />}

      {!isLoading && entries.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No audit entries found.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && entries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Timestamp</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Method</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Resource</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">IP</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Duration</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {new Date(entry.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-medium", getMethodColor(entry.action))}>
                        {entry.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs max-w-xs truncate" title={entry.resource}>
                      {entry.resource}
                    </td>
                    <td className={cn("px-4 py-3 font-medium", getStatusColor(entry.status_code))}>
                      {entry.status_code}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                      {entry.ip_address}
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">
                      {entry.duration_ms}ms
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({total} entries)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
